<template>
  <div class="form">
    <AHeading :level="1">パスワード再設定</AHeading>
    <form class="form__container" @submit.prevent="passwordEdit()">
      <div class="form__parts">
        <ALabel class="form__label" for="password">パスワード</ALabel>
        <ABody class="form__body" size="small">8文字以上の半角英数字記号</ABody>
        <AInput
          class="form__input"
          type="password"
          name="password"
          :value="password"
          placeholder="************"
          :validate-rule="passwordValidateRule"
          validate-mode="blur"
          @input="handleInput($event, 'password')"
        />
      </div>
      <div class="form__parts">
        <ALabel class="form__label" for="password">パスワードの再入力</ALabel>
        <AInput
          class="form__input"
          type="password"
          name="password_confirmation"
          :value="passwordConfirmation"
          :match-value="password"
          placeholder="確認のためにもう一度入力してください"
          :validate-rule="passwordValidateRule"
          validate-mode="blur"
          @input="handleInput($event, 'password_confirmation')"
        />
      </div>
      <div class="sform__button-wrapper">
        <AButton
          round
          fill
          bold
          size="medium"
          :disabled="disabled"
          :processing="processing"
          class="form__button"
          >パスワード再設定を完了する</AButton
        >
      </div>
    </form>
    <AModal v-if="showModal">
      <div class="password-edit__modal">
        <div class="password-edit__modal-icon">
          <CompleteCheckIcon />
        </div>
        <div class="password-edit__modal-body">
          <ABody>パスワード再設定が完了しました！</ABody>
        </div>
      </div>
    </AModal>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import * as api from '@/api/index';
import AButton from '@/components/atoms/AButton/index.vue';
import AModal from '@/components/atoms/AModal/index.vue';
import AHeading from '@/components/atoms/ATypography/AHeading/index.vue';
import ABody from '@/components/atoms/ATypography/ABody/index.vue';
import ALabel from '@/components/atoms/ATypography/ALabel/index.vue';
import AInput from '@/components/atoms/AForm/AInput/index.vue';
import CompleteCheckIcon from '@/assets/images/svg/icon/completeCheck.vue';
import { passwordValidateRule } from '@/helpers/validateRules';
import {
  ErrorAuthenticationMessage,
  ErrorExpirationDateMessage,
  ErrorSystemMessage
} from '@/helpers/errorMessages';

export default defineComponent({
  name: 'PasswordEdit',
  components: {
    AButton,
    AModal,
    AHeading,
    ABody,
    ALabel,
    AInput,
    CompleteCheckIcon
  },
  data() {
    return {
      password: '',
      passwordConfirmation: '',
      disabled: true,
      processing: false,
      resetPasswordToken: '',
      passwordValidateRule: passwordValidateRule,
      showModal: false
    };
  },
  computed: {
    passwordsMatch() {
      return !!this.password && this.password === this.passwordConfirmation;
    },
    buttonDisabled() {
      return !this.passwordsMatch || !!this.validateError;
    }
  },
  async created() {
    let reset_password_token = this.$route.query.reset_password_token;
    if (reset_password_token) {
      try {
        await api.post('/api/v1/password/verify', {
          reset_password_token: reset_password_token
        });
        this.resetPasswordToken = reset_password_token;
      } catch (error) {
        this.$router.push({
          name: 'users_password_new',
          params: {
            errorMessage: ErrorExpirationDateMessage
          }
        });
      }
    } else {
      this.$router.push({
        name: 'users_password_new',
        params: {
          errorMessage: ErrorAuthenticationMessage
        }
      });
    }
  },
  methods: {
    handleInput(value: string, name: string) {
      if (name === 'password') {
        this.password = value;
      } else if (name === 'password_confirmation') {
        this.passwordConfirmation = value;
      }
      this.disabled = this.buttonDisabled;
    },
    async passwordEdit() {
      if (!this.passwordsMatch) {
        return false;
      }

      try {
        this.processing = true;
        const response = await api
          .put('/api/v1/password/reset', {
            password: this.password,
            reset_password_token: this.resetPasswordToken
          })
          .finally(() => {
            this.processing = false;
          });
        if (response.data.access_token === undefined) {
          const register_token = response.data.register_token;
          window.location.href = `${process.env['VUE_APP_ONECAREER_PLUS_ORIGIN']}/users/invitation/accept?register_token=${register_token}`;
          return;
        }

        this.$cookies.set(
          'token',
          response.data.access_token,
          1800,
          '/',
          process.env['ONECAREERJP_DOMAIN'],
          { secure: true }
        );
        this.showModal = true;
        setTimeout(() => {
          this.$router.push('/');
        }, 1000);
      } catch (error) {
        alert(ErrorSystemMessage);
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.form {
  @include formBase;
}
.form__container {
  margin-top: 32px;
}
.password-edit {
  &__modal {
    text-align: center;
  }
  &__modal-icon {
    width: 142px;
    height: 142px;
    margin: auto;
  }
  &__modal-body {
    margin-top: 24px;
  }
}
</style>
